<script>
import { mapGetters } from 'vuex';
import PageHeader from '../../settings/SettingsSubPageHeader.vue';
import ReportModal from './components/SquadBiReportModal.vue';
import Spinner from 'shared/components/Spinner.vue';
import { SquadBiAPI } from 'dashboard/api/squadBi';

export default {
  name: 'SquadBiReports',
  components: {
    PageHeader,
    ReportModal,
    Spinner,
  },
  data() {
    return {
      reports: [],
      businessGroups: [],
      selectedBusinessGroup: '',
      selectedReport: null,
      showReportModal: false,
      isLoading: false,
      configuringGroup: false,
      authenticating: false,
      error: null,
      configuration: {
        configured: false,
        token_present: false,
        business_group: null,
        has_reports: false,
        credentials_configured: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    isAdmin() {
      return this.currentUser.role === 'administrator';
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      await this.fetchConfiguration();
      if (this.configuration.configured) {
        if (this.isAdmin) {
          await this.fetchBusinessGroups();
        }
        if (this.configuration.has_reports) {
          await this.fetchReports();
        }
      }
    },

    async fetchConfiguration() {
      try {
        const response = await SquadBiAPI.getConfiguration();
        this.configuration = response.data;
      } catch (error) {
        this.error =
          error.response?.data?.error ||
          this.$t('SQUAD_BI.CONFIGURATION_ERROR');
      }
    },

    async fetchBusinessGroups() {
      try {
        const response = await SquadBiAPI.getBusinessGroups();
        this.businessGroups = response.data.business_groups;
        this.selectedBusinessGroup = this.configuration.business_group || '';
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching business groups:', error);
      }
    },

    async fetchReports() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await SquadBiAPI.getReports();
        this.reports = response.data.reports;
      } catch (error) {
        this.error =
          error.response?.data?.error || this.$t('SQUAD_BI.FETCH_ERROR');
      } finally {
        this.isLoading = false;
      }
    },

    async configureBusinessGroup() {
      if (!this.selectedBusinessGroup) return;

      this.configuringGroup = true;

      try {
        await SquadBiAPI.configureBusinessGroup(this.selectedBusinessGroup);
        this.configuration.business_group = this.selectedBusinessGroup;
        this.configuration.has_reports = true;

        this.$toast.success(this.$t('SQUAD_BI.CONFIGURATION_SUCCESS'));
        await this.fetchReports();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.error || this.$t('SQUAD_BI.CONFIGURATION_ERROR')
        );
      } finally {
        this.configuringGroup = false;
      }
    },

    openReport(report) {
      this.selectedReport = report;
      this.showReportModal = true;
    },

    closeReportModal() {
      this.showReportModal = false;
      this.selectedReport = null;
    },

    async authenticateSquadBi() {
      this.authenticating = true;

      try {
        await SquadBiAPI.authenticate();
        await this.fetchConfiguration();
        this.$toast.success(this.$t('SQUAD_BI.AUTHENTICATION_SUCCESS'));
      } catch (error) {
        this.$toast.error(
          error.response?.data?.error ||
            this.$t('SQUAD_BI.AUTHENTICATION_ERROR')
        );
      } finally {
        this.authenticating = false;
      }
    },

    getConfigurationMessage() {
      if (!this.configuration.credentials_configured) {
        return this.$t('SQUAD_BI.CREDENTIALS_NOT_CONFIGURED');
      }
      if (!this.configuration.token_present) {
        return this.$t('SQUAD_BI.TOKEN_NOT_GENERATED');
      }
      return this.$t('SQUAD_BI.NOT_CONFIGURED_MESSAGE');
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <div class="flex flex-col">
      <PageHeader
        :header-title="$t('SQUAD_BI.TITLE')"
        :header-content="$t('SQUAD_BI.DESCRIPTION')"
      />

      <div v-if="!configuration.configured" class="mb-6">
        <div class="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <fluent-icon icon="warning" class="text-yellow-600 mr-3" />
              <div>
                <h3 class="text-sm font-medium text-yellow-800">
                  {{ $t('SQUAD_BI.NOT_CONFIGURED_TITLE') }}
                </h3>
                <p class="text-sm text-yellow-700 mt-1">
                  {{ getConfigurationMessage() }}
                </p>
              </div>
            </div>
            <div
              v-if="
                isAdmin &&
                configuration.credentials_configured &&
                !configuration.token_present
              "
            >
              <woot-button
                size="small"
                :is-loading="authenticating"
                @click="authenticateSquadBi"
              >
                {{ $t('SQUAD_BI.GENERATE_TOKEN') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isAdmin && configuration.configured" class="mb-6">
        <div class="bg-white rounded-lg border border-slate-200 p-6">
          <h3 class="text-lg font-medium text-slate-900 mb-4">
            {{ $t('SQUAD_BI.CONFIGURATION_TITLE') }}
          </h3>

          <div class="flex items-center space-x-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-slate-700 mb-2">
                {{ $t('SQUAD_BI.BUSINESS_GROUP_LABEL') }}
              </label>
              <select
                v-model="selectedBusinessGroup"
                class="w-full p-2 border border-slate-300 rounded-md"
                :disabled="configuringGroup"
              >
                <option value="">
                  {{ $t('SQUAD_BI.SELECT_BUSINESS_GROUP') }}
                </option>
                <option
                  v-for="group in businessGroups"
                  :key="group"
                  :value="group"
                >
                  {{ group }}
                </option>
              </select>
            </div>
            <woot-button
              :is-loading="configuringGroup"
              :is-disabled="!selectedBusinessGroup || configuringGroup"
              @click="configureBusinessGroup"
            >
              {{ $t('SQUAD_BI.CONFIGURE_BUTTON') }}
            </woot-button>
          </div>

          <div
            v-if="configuration.business_group"
            class="mt-4 text-sm text-slate-600"
          >
            {{ $t('SQUAD_BI.CURRENT_GROUP') }}:
            <span class="font-medium">{{ configuration.business_group }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="configuration.has_reports"
        class="bg-white rounded-lg border border-slate-200"
      >
        <div v-if="isLoading" class="p-8 text-center">
          <Spinner size="large" />
          <p class="mt-4 text-slate-600">
            {{ $t('SQUAD_BI.LOADING_REPORTS') }}
          </p>
        </div>

        <div v-else-if="error" class="p-8 text-center">
          <fluent-icon icon="error_circle" class="text-red-500 text-2xl mb-4" />
          <h3 class="text-lg font-medium text-slate-900 mb-2">
            {{ $t('SQUAD_BI.ERROR_TITLE') }}
          </h3>
          <p class="text-slate-600 mb-4">{{ error }}</p>
          <woot-button @click="fetchReports">
            {{ $t('SQUAD_BI.RETRY_BUTTON') }}
          </woot-button>
        </div>

        <div v-else-if="reports.length > 0" class="p-6">
          <h3 class="text-lg font-medium text-slate-900 mb-6">
            {{ $t('SQUAD_BI.AVAILABLE_REPORTS') }}
            <span class="text-sm font-normal text-slate-500">
              ({{ reports.length }})
            </span>
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="report in reports"
              :key="report.link"
              class="border border-slate-200 rounded-lg p-4 hover:border-woot-500 transition-colors cursor-pointer"
              @click="openReport(report)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <h4 class="font-medium text-slate-900 mb-2">
                    {{ report.report }}
                  </h4>
                  <div class="flex items-center text-sm text-slate-500">
                    <fluent-icon icon="external_link" class="mr-1" />
                    {{ $t('SQUAD_BI.CLICK_TO_VIEW') }}
                  </div>
                </div>
                <fluent-icon
                  icon="chevron_right"
                  class="text-slate-400 flex-shrink-0 ml-2"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="p-8 text-center">
          <fluent-icon
            icon="document_search"
            class="text-slate-400 text-2xl mb-4"
          />
          <h3 class="text-lg font-medium text-slate-900 mb-2">
            {{ $t('SQUAD_BI.NO_REPORTS_TITLE') }}
          </h3>
          <p class="text-slate-600">
            {{ $t('SQUAD_BI.NO_REPORTS_MESSAGE') }}
          </p>
        </div>
      </div>

      <div
        v-else-if="configuration.configured && !configuration.has_reports"
        class="bg-white rounded-lg border border-slate-200 p-8 text-center"
      >
        <fluent-icon icon="settings" class="text-slate-400 text-2xl mb-4" />
        <h3 class="text-lg font-medium text-slate-900 mb-2">
          {{ $t('SQUAD_BI.NO_GROUP_CONFIGURED_TITLE') }}
        </h3>
        <p class="text-slate-600 mb-4">
          {{ $t('SQUAD_BI.NO_GROUP_CONFIGURED_MESSAGE') }}
        </p>
        <woot-button v-if="isAdmin" @click="fetchBusinessGroups">
          {{ $t('SQUAD_BI.CONFIGURE_GROUP_BUTTON') }}
        </woot-button>
      </div>
    </div>

    <ReportModal
      v-if="selectedReport"
      :report="selectedReport"
      :is-visible="showReportModal"
      @close="closeReportModal"
    />
  </div>
</template>
